import React from 'react';
import {
  CardStyle,
  type CardProps,
  type CardHeaderProps,
  type CardBodyProps,
  type CardMediaProps,
  type CardContentProps,
  type CardFooterProps,
  type CardActionProps,
} from './types';
import { useCard, paddingConfig, alignConfig } from './useCard';

// ============================================================================
// Icons
// ============================================================================

// Menu icon (3 dots vertical)
const MenuIcon: React.FC<{ className?: string }> = ({ className }) => (
  <svg
    className={className}
    viewBox="0 0 24 24"
    fill="currentColor"
    xmlns="http://www.w3.org/2000/svg"
  >
    <circle cx="12" cy="5" r="2" />
    <circle cx="12" cy="12" r="2" />
    <circle cx="12" cy="19" r="2" />
  </svg>
);

// Default Avatar placeholder
const DefaultAvatar: React.FC<{ className?: string }> = ({ className }) => (
  <div
    className={`
      flex items-center justify-center
      bg-primary-color-90 text-primary-color-40
      rounded-full font-medium
      ${className || ''}
    `}
    style={{ width: 40, height: 40, fontSize: 16 }}
  >
    A
  </div>
);

// ============================================================================
// Card Container Component
// ============================================================================

const Card: React.FC<CardProps> = ({
  style = CardStyle.Elevated,
  clickable = false,
  onClick,
  className = '',
  width = 360,
  children,
}) => {
  const { styleClasses, handleClick } = useCard({ style, clickable, onClick });

  return (
    <div
      className={`
        rounded-xl overflow-hidden
        transition-all duration-200
        ${styleClasses.container}
        ${clickable ? 'cursor-pointer' : ''}
        ${className}
      `}
      style={{
        width: typeof width === 'number' ? `${width}px` : width,
        fontFamily: 'Roboto, sans-serif',
      }}
      onClick={handleClick}
      role={clickable ? 'button' : undefined}
      tabIndex={clickable ? 0 : undefined}
    >
      {children}
    </div>
  );
};

// ============================================================================
// Card Header Component
// ============================================================================

const CardHeader: React.FC<CardHeaderProps> = ({
  avatar,
  title,
  subtitle,
  showMenu = true,
  onMenuClick,
  action,
  className = '',
  children,
}) => {
  // If children are provided, render them directly
  if (children) {
    return <div className={`p-4 ${className}`}>{children}</div>;
  }

  return (
    <div className={`flex items-center gap-3 p-4 ${className}`}>
      {/* Avatar */}
      {(avatar || avatar === undefined) && (
        <div className="shrink-0">{avatar || <DefaultAvatar />}</div>
      )}

      {/* Header Text */}
      <div className="flex-1 min-w-0">
        {title && (
          <p
            className="text-neutral-10 font-medium truncate"
            style={{
              fontSize: 16,
              lineHeight: '24px',
            }}
          >
            {title}
          </p>
        )}
        {subtitle && (
          <p
            className="text-neutral-40 truncate"
            style={{
              fontSize: 14,
              lineHeight: '20px',
            }}
          >
            {subtitle}
          </p>
        )}
      </div>

      {/* Action / Menu Icon */}
      {action ? (
        <div className="shrink-0">{action}</div>
      ) : showMenu ? (
        <button
          type="button"
          onClick={onMenuClick}
          className="shrink-0 p-2 rounded-full hover:bg-neutral-90 transition-colors text-neutral-40"
        >
          <MenuIcon className="w-5 h-5" />
        </button>
      ) : null}
    </div>
  );
};

// ============================================================================
// Card Body Component
// ============================================================================

const CardBody: React.FC<CardBodyProps> = ({
  className = '',
  padding = 'none',
  children,
}) => {
  return (
    <div className={`${paddingConfig[padding]} ${className}`}>{children}</div>
  );
};

// ============================================================================
// Card Media Component (for use inside CardBody)
// ============================================================================

const CardMedia: React.FC<CardMediaProps> = ({
  src,
  alt = '',
  children,
  height = 194,
  className = '',
}) => {
  return (
    <div
      className={`w-full bg-neutral-90 overflow-hidden flex items-center justify-center ${className}`}
      style={{ height }}
    >
      {src ? (
        <img src={src} alt={alt} className="w-full h-full object-cover" />
      ) : children ? (
        children
      ) : (
        // Default placeholder with shapes
        <div className="flex items-center justify-center gap-4 text-neutral-60">
          <svg width="48" height="48" viewBox="0 0 48 48" fill="currentColor">
            <polygon points="24,8 40,40 8,40" />
          </svg>
          <svg width="48" height="48" viewBox="0 0 48 48" fill="currentColor">
            <path d="M24 4 L30 16 L44 16 L33 26 L37 40 L24 32 L11 40 L15 26 L4 16 L18 16 Z" />
          </svg>
          <svg width="48" height="48" viewBox="0 0 48 48" fill="currentColor">
            <circle cx="24" cy="24" r="18" />
          </svg>
        </div>
      )}
    </div>
  );
};

// ============================================================================
// Card Content Component (for use inside CardBody)
// ============================================================================

const CardContent: React.FC<CardContentProps> = ({
  title,
  subtitle,
  description,
  descriptionClassName = '',
  className = '',
  children,
}) => {
  return (
    <div className={`px-4 py-3 ${className}`}>
      {title && (
        <p
          className=" font-medium"
          style={{
            fontSize: 18,
            lineHeight: '26px',
          }}
        >
          {title}
        </p>
      )}
      {subtitle && (
        <p
          className="text-neutral-40"
          style={{
            fontSize: 14,
            lineHeight: '20px',
          }}
        >
          {subtitle}
        </p>
      )}
      {description && (
        <p
          className={`mt-2 ${descriptionClassName}`}
          style={{
            fontSize: 14,
            lineHeight: '20px',
          }}
        >
          {description}
        </p>
      )}
      {children}
    </div>
  );
};

// ============================================================================
// Card Footer Component
// ============================================================================

const CardFooter: React.FC<CardFooterProps> = ({
  align = 'right',
  className = '',
  children,
}) => {
  return (
    <div
      className={`flex items-center gap-2 px-4 pb-4 pt-2 ${alignConfig[align]} ${className}`}
    >
      {children}
    </div>
  );
};

// ============================================================================
// Card Action Button Component (for use inside CardFooter)
// ============================================================================

const CardAction: React.FC<CardActionProps> = ({
  label,
  variant = 'secondary',
  onClick,
  disabled = false,
  className = '',
}) => {
  return (
    <button
      type="button"
      disabled={disabled}
      onClick={onClick}
      className={`
        px-4 py-2 rounded-full font-medium text-sm
        transition-all duration-200
        ${
          variant === 'primary'
            ? 'bg-primary-color-40 text-neutral-100 hover:bg-primary-color-50'
            : 'bg-transparent border border-neutral-70 text-neutral-30 hover:bg-neutral-95'
        }
        ${disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}
        ${className}
      `}
      style={{
        fontSize: 14,
        lineHeight: '20px',
        fontWeight: 500,
      }}
    >
      {label}
    </button>
  );
};

// ============================================================================
// Exports
// ============================================================================

export {
  Card,
  CardHeader,
  CardBody,
  CardMedia,
  CardContent,
  CardFooter,
  CardAction,
};

export default Card;
